import {
	getMomentTz,
	MESApiService,
	type spParameter,
} from "@dexteel/mesf-core";
import { get } from "lodash-es";
import type {
	WaterBill,
	WaterBillAllocation,
	WaterBillDraft,
	WaterBillMeter,
	WaterBillsData,
	WaterBillTarget,
	WaterBillTrackedGallons,
	WaterReadType,
} from "../models/WaterBilling";

const moment = getMomentTz();

export const getWaterBills = async (
	params: {
		waterAccountId: number;
		startDate: Date | null;
		endDate: Date | null;
	},
	signal?: AbortSignal,
): Promise<WaterBillsData> => {
	const apiService = new MESApiService();

	const resp = await apiService.callV2(
		"[USG].[GetWaterBills]",
		[
			{ name: "WaterAccountId", value: params.waterAccountId },
			{ name: "StartDate", value: params.startDate?.toISOString() ?? null },
			{ name: "EndDate", value: params.endDate?.toISOString() ?? null },
		],
		signal,
	);

	if (resp.ok) {
		const tables = get(resp, "data.tables", {});

		return {
			bills: (get(tables, "0.rows", []) as WaterBill[]).map((row) => ({
				BillId: row.BillId,
				WaterAccountId: row.WaterAccountId,
				AccountNumber: row.AccountNumber,
				Location: row.Location,
				ServicePeriodStart: row.ServicePeriodStart
					? moment.utc(row.ServicePeriodStart).toDate()
					: null,
				ServicePeriodEnd: row.ServicePeriodEnd
					? moment.utc(row.ServicePeriodEnd).toDate()
					: null,
				TotalGallons: row.TotalGallons,
				TrackedGallons: row.TrackedGallons,
				BalanceForward: row.BalanceForward,
				CurrentBill: row.CurrentBill,
				CostPerGallon: row.CostPerGallon,
				AmountDue: row.AmountDue,
				CheckNumber: row.CheckNumber,
				PayDate: row.PayDate ? moment.utc(row.PayDate).toDate() : null,
				IsPaid: row.IsPaid,
				Comments: row.Comments,
				MeterCount: row.MeterCount,
			})),
			meters: (get(tables, "1.rows", []) as WaterBillMeter[]).map((row) => ({
				BillId: row.BillId,
				MeterId: row.MeterId,
				ExternalMeterId: row.ExternalMeterId,
				MeterNumber: row.MeterNumber,
				MeterSize: row.MeterSize,
				AssetId: row.AssetId,
				MillName: row.MillName,
				Gallons: row.Gallons,
				TrackedGallons: row.TrackedGallons,
				StartReadType: row.StartReadType as WaterReadType | null,
				EndReadType: row.EndReadType as WaterReadType | null,
				StartDate: row.StartDate ? moment.utc(row.StartDate).toDate() : null,
				EndDate: row.EndDate ? moment.utc(row.EndDate).toDate() : null,
				IsDateOverridden: row.IsDateOverridden,
			})),
			allocations: (get(tables, "2.rows", []) as WaterBillAllocation[]).map(
				(row) => ({
					BillId: row.BillId,
					TargetId: row.TargetId,
					TargetCode: row.TargetCode,
					TargetName: row.TargetName,
					AssetId: row.AssetId,
					AllocationPercent: row.AllocationPercent,
					AllocatedGallons: row.AllocatedGallons,
					AllocatedAmount: row.AllocatedAmount,
					DisplayOrder: row.DisplayOrder,
				}),
			),
			targets: get(tables, "3.rows", []) as WaterBillTarget[],
		} as WaterBillsData;
	} else {
		throw new Error(resp.message || "Error fetching water bills");
	}
};

export const getWaterBillTrackedGallons = async (
	billId: number,
	startDateKey: string,
	endDateKey: string,
	signal?: AbortSignal,
): Promise<WaterBillTrackedGallons> => {
	const apiService = new MESApiService();

	const resp = await apiService.callV2(
		"[USG].[GetWaterBillTrackedGallons]",
		[
			{ name: "BillId", value: billId },
			{ name: "ServicePeriodStart", value: startDateKey },
			{ name: "ServicePeriodEnd", value: endDateKey },
		],
		signal,
	);

	if (resp.ok) {
		const lines = get(resp, "data.tables.0.rows", []) as {
			MeterId: number;
			TrackedGallons: number | null;
		}[];

		return {
			byMeterId: Object.fromEntries(
				lines.map((line) => [line.MeterId, line.TrackedGallons]),
			),
			total: get(resp, "data.tables.1.rows.0.TrackedGallons", null) as
				| number
				| null,
		};
	}

	throw new Error(resp.message || "Error fetching tracked gallons");
};

export const upsertWaterBill = async (draft: WaterBillDraft) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "BillId", value: draft.BillId },
		{ name: "WaterAccountId", value: draft.WaterAccountId },
		{
			name: "ServicePeriodStart",
			value: draft.ServicePeriodStart?.toISOString() || null,
		},
		{
			name: "ServicePeriodEnd",
			value: draft.ServicePeriodEnd?.toISOString() || null,
		},
		{ name: "BalanceForward", value: draft.BalanceForward },
		{ name: "CurrentBill", value: draft.CurrentBill },
		{ name: "CheckNumber", value: draft.CheckNumber },
		{
			name: "PayDate",
			value: draft.PayDate?.toISOString() || null,
		},
		{ name: "MetersJson", value: toMetersJson(draft.Meters) },
		{ name: "AllocationsJson", value: toAllocationsJson(draft.Allocations) },
	];

	const resp = await apiService.callV2("[USG].[UpsertWaterBill]", parameters);

	if (!resp.ok) {
		throw new Error(resp.message || "Error saving the water bill");
	}

	return get(resp, "data.tables.0.rows.0.BillId", null) as number | null;
};

export const deleteWaterBill = async (billId: number) => {
	const apiService = new MESApiService();
	const resp = await apiService.callV2("[USG].[DeleteWaterBill]", [
		{ name: "BillId", value: billId },
	]);

	if (!resp.ok) {
		throw new Error(resp.message || "Error deleting the water bill");
	}
};

const toMetersJson = (meters: WaterBillDraft["Meters"]) =>
	meters === null ? null : JSON.stringify(meters);

const toAllocationsJson = (allocations: WaterBillDraft["Allocations"]) =>
	allocations === null ? null : JSON.stringify(allocations);
